import { useEffect, useMemo, useState } from 'react';

export default function MenuEditor({
  pages,
  menu,
  menuBackgroundColor,
  menuButtonColor,
  menuTextColor,
  onAdd,
  onRemove,
  onMove,
  onRename,
  onSetParent,
  onSetStyle,
}) {
  const [editingId, setEditingId] = useState(null);
  const [editingLabel, setEditingLabel] = useState('');
  const [selectedParentPageId, setSelectedParentPageId] = useState('');
  const [bgColor, setBgColor] = useState(menuBackgroundColor || '#ffffff');
  const [btnColor, setBtnColor] = useState(menuButtonColor || '#004165');
  const [txtColor, setTxtColor] = useState(menuTextColor || '#111111');

  useEffect(() => {
    setBgColor(menuBackgroundColor || '#ffffff');
    setBtnColor(menuButtonColor || '#004165');
    setTxtColor(menuTextColor || '#111111');
  }, [menuBackgroundColor, menuButtonColor, menuTextColor]);

  const inMenuIds = new Set(menu.map((item) => item.pageId));
  const availablePages = pages.filter((p) => !inMenuIds.has(p.id));

  const labelByPageId = useMemo(() => {
    const m = new Map();
    menu.forEach((item) => m.set(item.pageId, item.label));
    return m;
  }, [menu]);

  const startEdit = (item) => {
    setEditingId(item.pageId);
    setEditingLabel(item.label);
  };

  const saveEdit = () => {
    if (editingLabel.trim()) onRename(editingId, editingLabel.trim());
    setEditingId(null);
    setEditingLabel('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingLabel('');
  };

  return (
    <div className="pagebuilder-menu-editor">
      <div className="pagebuilder-menu-header">
        <i className="bi bi-list-ul me-2" aria-hidden="true" />
        <span>Navigation Menu</span>
      </div>
      <p className="pagebuilder-menu-desc">
        Pages listed here will appear in the site's navigation. Reorder with the arrows or rename the link labels.
      </p>

      <div className="pagebuilder-field-row mb-3">
        <div>
          <label className="form-label" htmlFor="menu-bg-color">Menu Background Color</label>
          <div className="pagebuilder-field-row">
            <input
              id="menu-bg-color"
              type="color"
              className="form-control form-control-color"
              value={bgColor}
              onChange={(e) => setBgColor(e.target.value)}
              title="Menu background color"
            />
          </div>
        </div>

        <div>
          <label className="form-label" htmlFor="menu-btn-color">Menu Button Color</label>
          <div className="pagebuilder-field-row">
            <input
              id="menu-btn-color"
              type="color"
              className="form-control form-control-color"
              value={btnColor}
              onChange={(e) => setBtnColor(e.target.value)}
              title="Menu button color"
            />
          </div>
        </div>

        <div>
          <label className="form-label" htmlFor="menu-text-color">Menu Text Color</label>
          <div className="pagebuilder-field-row">
            <input
              id="menu-text-color"
              type="color"
              className="form-control form-control-color"
              value={txtColor}
              onChange={(e) => setTxtColor(e.target.value)}
              title="Menu text color"
            />
          </div>
        </div>

        <div style={{ alignSelf: 'end' }}>
          <button
            type="button"
            className="pagebuilder-add-btn pagebuilder-add-btn--sm"
            onClick={() => onSetStyle({ menuBackgroundColor: bgColor, menuButtonColor: btnColor, menuTextColor: txtColor })}
          >
            Apply Colors
          </button>
        </div>
      </div>

      {menu.length === 0 ? (
        <p className="pagebuilder-menu-empty">No pages in the menu yet. Add pages using the list below.</p>
      ) : (
        <div className="pagebuilder-menu-list">
          {menu.map((item, idx) => (
            <div key={item.pageId} className="pagebuilder-menu-item">
              <span className="pagebuilder-menu-order">{idx + 1}</span>

              {editingId === item.pageId ? (
                <input
                  className="pagebuilder-page-input pagebuilder-page-input--inline"
                  value={editingLabel}
                  onChange={(e) => setEditingLabel(e.target.value)}
                  onBlur={saveEdit}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                  autoFocus
                />
              ) : (
                <div className="pagebuilder-menu-label-wrap">
                  <span className="pagebuilder-menu-label">
                    {item.icon ? <i className={`${item.icon} me-1`} aria-hidden="true" /> : null}
                    {item.label}
                  </span>
                  {item.parentPageId && (
                    <small className="pagebuilder-menu-subpage-note">Subpage of {labelByPageId.get(item.parentPageId) || item.parentPageId}</small>
                  )}
                </div>
              )}

              <select
                className="pagebuilder-page-input pagebuilder-page-input--inline"
                value={item.parentPageId || ''}
                onChange={(e) => onSetParent(item.pageId, e.target.value || null)}
                title="Set parent page"
              >
                <option value="">Top-level</option>
                {menu.filter((m) => m.pageId !== item.pageId).map((m) => (
                  <option key={m.pageId} value={m.pageId}>{m.label}</option>
                ))}
              </select>

              <div className="pagebuilder-block-actions">
                <button
                  type="button"
                  className="pagebuilder-block-action-btn"
                  onClick={() => startEdit(item)}
                  title="Edit label"
                  aria-label="Edit menu label"
                >
                  <i className="bi bi-pencil" />
                </button>
                <button
                  type="button"
                  className="pagebuilder-block-action-btn"
                  onClick={() => onMove(item.pageId, -1)}
                  disabled={idx === 0}
                  title="Move up"
                  aria-label="Move menu item up"
                >
                  <i className="bi bi-arrow-up" />
                </button>
                <button
                  type="button"
                  className="pagebuilder-block-action-btn"
                  onClick={() => onMove(item.pageId, 1)}
                  disabled={idx === menu.length - 1}
                  title="Move down"
                  aria-label="Move menu item down"
                >
                  <i className="bi bi-arrow-down" />
                </button>
                <button
                  type="button"
                  className="pagebuilder-block-action-btn pagebuilder-block-action-btn--danger"
                  onClick={() => onRemove(item.pageId)}
                  title="Remove from menu"
                  aria-label="Remove from menu"
                >
                  <i className="bi bi-x-lg" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {availablePages.length > 0 && (
        <div className="pagebuilder-menu-add-section">
          <p className="pagebuilder-add-panel-label">Add page to menu:</p>
          <div className="pagebuilder-field-row mb-2">
            <select
              className="pagebuilder-page-input pagebuilder-page-input--inline"
              value={selectedParentPageId}
              onChange={(e) => setSelectedParentPageId(e.target.value)}
              title="Select parent for new menu items"
            >
              <option value="">Top-level</option>
              {menu.map((m) => (
                <option key={m.pageId} value={m.pageId}>{m.label}</option>
              ))}
            </select>
          </div>
          <div className="pagebuilder-menu-avail">
            {availablePages.map((page) => (
              <button
                key={page.id}
                type="button"
                className="pagebuilder-menu-avail-btn"
                onClick={() => onAdd(page.id, selectedParentPageId || null)}
              >
                <i className="bi bi-plus-lg" aria-hidden="true" />
                {page.title}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
